/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect } from 'react';
import { usePrevious } from '../../../../hooks/usePrevious';
import { useRootSelector } from '../../../../hooks/useRootSelector';
import { get } from '@utils/lodash';
import { SigningActionType } from '@shared/constants/signing';

const refetchPdfAfterSigning = 1000;

export const useDocumentViewing = (setSelectedDocumentAndFetchPdf: (documentId: string) => void) => {
  const { signingRoom, document } = useRootSelector(state => state);
  const { signingAction } = signingRoom;
  const { documents, selectedDoc } = document;

  const selectedDocument = documents?.find(doc => doc.id === selectedDoc?.id);

  // use previous signingAction to determine whether or not to auto-transition
  const prevAction = usePrevious<typeof signingAction>(signingAction);

  useEffect(() => {
    const signingJustStarted =
      signingAction.type === SigningActionType.VIEW &&
      prevAction &&
      prevAction.type === SigningActionType.WAIT_FOR_SIGNING_TO_START;

    // if signing just started, set the signingAction doc as the selected doc
    // if signing just completed, refetch doc to get updated signatures
    if (signingJustStarted && signingAction?.document?.id) {
      setSelectedDocumentAndFetchPdf(signingAction.document.id);
      return;
    }

    const transitionedToDone =
      signingAction.type === SigningActionType.DONE && prevAction && prevAction.type !== SigningActionType.DONE;
    // TODO: (RON-580) handle VIEW -> VIEW case with multiple participants to refresh each time there is a new signature
    const transitionedFromSigningToViewing =
      signingAction.type === SigningActionType.VIEW && prevAction && prevAction.type === SigningActionType.SIGN;

    // In this case, we want to re-fetch the PDF to show the latest signatures
    // We do not want to auto-transition to the next document (we don't do this until the signer is the active signer)
    if ((transitionedFromSigningToViewing || transitionedToDone) && selectedDocument?.id) {
      // TODO: (RON-580) rely on events from Documents service for this refresh as there is no guarantee the signatures will appear
      // wait a bit before re-fetching PDF again to show signatures that were just applied
      setTimeout(() => {
        setSelectedDocumentAndFetchPdf(selectedDocument.id);
      }, refetchPdfAfterSigning);
    }

    // make sure to show the correct document if user refreshes the page
    if (signingAction.type === SigningActionType.REVIEW && signingAction?.document?.id) {
      setSelectedDocumentAndFetchPdf(signingAction.document.id);
    }
  }, [signingAction.type, get(signingAction.document, 'id'), selectedDocument?.id, prevAction?.type]);

  return { selectedDocument };
};
